import { accountKeys } from '@taler/api-client'
import { type CurrencyCode, isCurrencyCode } from '@taler/domain'
import { useQuery } from '@tanstack/vue-query'
import { computed, ref, watch } from 'vue'
import { useApi } from './api.ts'
import { useSession } from './session.ts'

const STORAGE_KEY = 'taler.displayCurrency'
/** Used only when the active ledger has no default payment account, or that account has no currency. */
export const DEFAULT_DISPLAY_CURRENCY: CurrencyCode = 'EUR'

function readStored(): CurrencyCode | undefined {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY) ?? ''
    return isCurrencyCode(stored) ? stored : undefined
  } catch {
    return undefined
  }
}

const stored = ref<CurrencyCode | undefined>(readStored())
const working = ref<CurrencyCode | undefined>(undefined)
const current = computed(() => stored.value ?? working.value ?? DEFAULT_DISPLAY_CURRENCY)

/** One app-wide display currency; every screen reads it, none owns its own. */
export function useDisplayCurrency() {
  function set(next: CurrencyCode): void {
    stored.value = next
    try {
      window.localStorage.setItem(STORAGE_KEY, next)
    } catch {
      // choice simply does not persist
    }
  }
  return { displayCurrency: current, setDisplayCurrency: set }
}

/** Feeds the active ledger's working currency (its default payment account's) into the default. */
export function useLedgerWorkingCurrency(): void {
  const api = useApi()
  const { activeLedger } = useSession()
  const ledgerId = computed(() => activeLedger.value?.id)
  const paymentAccountId = computed(() => activeLedger.value?.defaultPaymentAccountId)

  const paymentAccount = useQuery({
    queryKey: computed(() => accountKeys.detail(ledgerId.value ?? '', paymentAccountId.value ?? '')),
    queryFn: ({ signal }) => api.accounts.get(ledgerId.value!, paymentAccountId.value!, signal),
    enabled: computed(() => ledgerId.value !== undefined && paymentAccountId.value !== undefined),
    retry: false,
  })

  watch(
    [ledgerId, paymentAccountId, () => paymentAccount.data.value, () => paymentAccount.isError.value],
    () => {
      if (ledgerId.value === undefined) return
      const account = paymentAccount.data.value
      if (paymentAccountId.value === undefined || paymentAccount.isError.value) {
        working.value = undefined
      } else if (account !== undefined) {
        working.value =
          account.currency !== undefined && isCurrencyCode(account.currency) ? account.currency : undefined
      }
    },
    { immediate: true },
  )
}
